import { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  evaluateExpansionGates,
  getExpansionDashboard,
  listExpansionSeedBatches,
  refreshExpansionReadiness,
} from "../../api/clientVendorDirectoryExpansionApi";
import {
  hasVendorDirectoryPermission,
  VENDOR_DIRECTORY_PERMISSIONS as P,
} from "../../auth/casamapaVendorDirectoryPermissions";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.expansion";

function translateEnum(t, prefix, value) {
  if (!value) return "—";
  const key = `${prefix}.${value}`;
  const translated = t(key);
  return translated !== key ? translated : formatStatusLabel(value);
}

function expansionStatusBadgeClass(status) {
  if (status === "public") return "cvd-badge--published";
  if (status === "paused" || status === "rolled_back") return "cvd-badge--suspended";
  if (["limited_pilot", "vendor_preview", "internal_validation"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function riskBadgeClass(risk) {
  if (risk === "standard") return "cvd-badge--draft";
  if (risk === "elevated") return "cvd-badge--warning";
  return "cvd-badge--suspended";
}

function ProgressBar({ value, label }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  return (
    <div className="cvd-expansion-hub__progress">
      <div className="cvd-expansion-hub__progress-head">
        <span>{label}</span>
        <strong>{pct}%</strong>
      </div>
      <div className="cvd-expansion-hub__progress-track" aria-hidden="true">
        <span className="cvd-expansion-hub__progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ExpansionShell({ base, children }) {
  const { t } = useLocale();
  return (
    <div className="page-content cvd-admin cvd-expansion-hub">
      <ClientVendorDirectorySubnav />
      <nav className="cvd-subnav cvd-expansion-hub__subnav" aria-label={t(`${COPY_PREFIX}.navLabel`)}>
        <NavLink to={base} end>
          {t(`${COPY_PREFIX}.nav.overview`)}
        </NavLink>
        <NavLink to={`${base}/categorias`}>{t(`${COPY_PREFIX}.nav.categories`)}</NavLink>
        <NavLink to={`${base}/lotes`}>{t(`${COPY_PREFIX}.nav.batches`)}</NavLink>
      </nav>
      {children}
    </div>
  );
}

function ExpansionHeader({ viewName, stats = null }) {
  const { t } = useLocale();
  return (
    <header className={`cvd-expansion-hub__hero${stats ? "" : " cvd-expansion-hub__hero--simple"}`}>
      <div className="cvd-expansion-hub__hero-copy">
        <h1>{t(`${COPY_PREFIX}.title`)}</h1>
        <p>{t(`${COPY_PREFIX}.intro`, { viewName: viewName || "CasaMapa" })}</p>
      </div>
      {stats ? (
        <dl className="cvd-expansion-hub__hero-stats">
          {stats.map((stat) => (
            <div key={stat.label}>
              <dt>{stat.label}</dt>
              <dd className={stat.tone === "success" ? "is-ready" : undefined}>{stat.value ?? "—"}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </header>
  );
}

function useCategoryLabel() {
  const { t } = useLocale();
  return (categoryKey) => translateEnum(t, `${COPY_PREFIX}.categoryLabels`, categoryKey);
}

function CategorySummaryCard({ category, categoryLabel }) {
  const { t } = useLocale();
  const readiness = category.readiness || {};
  const gates = category.launchGates || {};

  return (
    <article className="cvd-expansion-hub__category-card">
      <header className="cvd-expansion-hub__category-card-head">
        <div>
          <h3>{categoryLabel(category.categoryKey)}</h3>
          <p className="cvd-expansion-hub__category-meta">
            {translateEnum(t, `${COPY_PREFIX}.seedWave`, category.seedWave)} ·{" "}
            {t(`${COPY_PREFIX}.overview.seedBatches`, { count: category.seedBatches ?? 0 })}
          </p>
        </div>
        <div className="cvd-expansion-hub__badge-row">
          <span className={`cvd-badge ${expansionStatusBadgeClass(category.expansionStatus)}`}>
            {translateEnum(t, `${COPY_PREFIX}.status`, category.expansionStatus)}
          </span>
          <span className={`cvd-badge ${riskBadgeClass(category.riskLevel)}`}>
            {translateEnum(t, `${COPY_PREFIX}.risk`, category.riskLevel)}
          </span>
        </div>
      </header>
      <ProgressBar
        value={readiness.percentComplete}
        label={t(`${COPY_PREFIX}.overview.readiness`)}
      />
      <p className="cvd-expansion-hub__gates-copy">
        {t(`${COPY_PREFIX}.overview.launchGates`, {
          passed: gates.passed ?? 0,
          total: gates.total ?? 0,
        })}
        {gates.canActivate ? (
          <span className="cvd-expansion-hub__ready-badge">{t(`${COPY_PREFIX}.overview.launchReady`)}</span>
        ) : null}
      </p>
    </article>
  );
}

function OverviewPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const categoryLabel = useCategoryLabel();
  const base = `${serviciosBase}/expansion`;
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getExpansionDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch {
      setErrorMessage(t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = dashboard?.summary || {};

  return (
    <ExpansionShell base={base}>
      <ExpansionHeader
        viewName={activeView?.name}
        stats={
          dashboard
            ? [
                { label: t(`${COPY_PREFIX}.metrics.total`), value: summary.total },
                {
                  label: t(`${COPY_PREFIX}.metrics.public`),
                  value: summary.public,
                  tone: summary.public > 0 ? "success" : "default",
                },
                { label: t(`${COPY_PREFIX}.metrics.planning`), value: summary.planning },
                {
                  label: t(`${COPY_PREFIX}.metrics.launchReady`),
                  value: summary.launchReady,
                  tone: summary.launchReady > 0 ? "success" : "default",
                },
              ]
            : null
        }
      />
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && dashboard ? (
        <section className="cvd-expansion-hub__section" aria-label={t(`${COPY_PREFIX}.overview.categoriesTitle`)}>
          <div className="cvd-expansion-hub__category-grid">
            {dashboard.categories.map((category) => (
              <CategorySummaryCard
                key={category.categoryKey}
                category={category}
                categoryLabel={categoryLabel}
              />
            ))}
          </div>
        </section>
      ) : null}
    </ExpansionShell>
  );
}

function CategoriesPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const { permissions } = useVendorDirectoryPermissions();
  const categoryLabel = useCategoryLabel();
  const base = `${serviciosBase}/expansion`;
  const canConfigure = hasVendorDirectoryPermission(permissions, P.EXPANSION_CONFIGURE);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [evaluating, setEvaluating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getExpansionDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch {
      setErrorMessage(t(`${COPY_PREFIX}.categoriesLoadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRefresh() {
    setRefreshing(true);
    setErrorMessage("");
    try {
      await refreshExpansionReadiness(clientViewId);
      const body = await getExpansionDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch {
      setErrorMessage(t(`${COPY_PREFIX}.refreshError`));
    } finally {
      setRefreshing(false);
    }
  }

  async function handleEvaluate(categoryKey) {
    setEvaluating(categoryKey);
    setErrorMessage("");
    try {
      await evaluateExpansionGates(clientViewId, categoryKey);
      await load();
    } catch {
      setErrorMessage(t(`${COPY_PREFIX}.evaluateError`));
    } finally {
      setEvaluating(null);
    }
  }

  return (
    <ExpansionShell base={base}>
      <ExpansionHeader viewName={activeView?.name} />
      <div className="cvd-expansion-hub__section-head">
        <div>
          <h2 className="cvd-expansion-hub__section-title">{t(`${COPY_PREFIX}.categoriesPage.title`)}</h2>
          <p className="cvd-expansion-hub__section-hint">{t(`${COPY_PREFIX}.categoriesPage.intro`)}</p>
        </div>
        {canConfigure ? (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading || refreshing}
            onClick={handleRefresh}
          >
            {refreshing
              ? t(`${COPY_PREFIX}.categoriesPage.refreshing`)
              : t(`${COPY_PREFIX}.categoriesPage.refresh`)}
          </button>
        ) : null}
      </div>
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.categoriesPage.loading`)} /> : null}
      {refreshing ? <ClientPortalLoading message={t(`${COPY_PREFIX}.categoriesPage.refreshing`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && dashboard ? (
        <div className="cvd-expansion-hub__stack">
          {dashboard.categories.map((category) => {
            const checklist = Object.values(category.readiness?.checklist || {});
            const passed = checklist.filter((item) => item.passed).length;
            return (
              <article key={category.categoryKey} className="cvd-expansion-hub__detail-card">
                <header className="cvd-expansion-hub__category-card-head">
                  <div>
                    <h3>{categoryLabel(category.categoryKey)}</h3>
                    <p className="cvd-expansion-hub__category-meta">
                      {translateEnum(t, `${COPY_PREFIX}.seedWave`, category.seedWave)} ·{" "}
                      {translateEnum(t, `${COPY_PREFIX}.risk`, category.riskLevel)}
                    </p>
                  </div>
                  <span className={`cvd-badge ${expansionStatusBadgeClass(category.expansionStatus)}`}>
                    {translateEnum(t, `${COPY_PREFIX}.status`, category.expansionStatus)}
                  </span>
                </header>
                <ProgressBar
                  value={category.readiness?.percentComplete}
                  label={t(`${COPY_PREFIX}.categoriesPage.readinessProgress`, { passed, total: checklist.length })}
                />
                <ul className="cvd-expansion-hub__checklist">
                  {checklist.map((item) => (
                    <li
                      key={item.key}
                      className={item.passed ? "cvd-expansion-hub__checklist-item--done" : "cvd-expansion-hub__checklist-item--pending"}
                    >
                      <span className="cvd-expansion-hub__checklist-icon" aria-hidden="true">
                        {item.passed ? "✓" : "○"}
                      </span>
                      <div>
                        <strong>{translateEnum(t, `${COPY_PREFIX}.checklist`, item.key)}</strong>
                        <p>{item.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                {canConfigure ? (
                  <div className="cvd-expansion-hub__card-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={evaluating === category.categoryKey || refreshing}
                      onClick={() => handleEvaluate(category.categoryKey)}
                    >
                      {evaluating === category.categoryKey
                        ? t(`${COPY_PREFIX}.categoriesPage.evaluating`)
                        : t(`${COPY_PREFIX}.categoriesPage.evaluateGates`)}
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}
    </ExpansionShell>
  );
}

function BatchesPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const categoryLabel = useCategoryLabel();
  const base = `${serviciosBase}/expansion`;
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listExpansionSeedBatches(clientViewId);
      setBatches(body.batches || []);
    } catch {
      setErrorMessage(t(`${COPY_PREFIX}.batchesLoadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ExpansionShell base={base}>
      <ExpansionHeader viewName={activeView?.name} />
      <h2 className="cvd-expansion-hub__section-title">{t(`${COPY_PREFIX}.batches.title`)}</h2>
      <p className="cvd-expansion-hub__section-hint">{t(`${COPY_PREFIX}.batches.intro`)}</p>
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.batches.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading ? (
        batches.length > 0 ? (
          <div className="cvd-expansion-hub__batch-grid">
            {batches.map((batch) => (
              <article key={batch.id} className="cvd-expansion-hub__batch-card">
                <header>
                  <h3>{batch.name}</h3>
                  <span className={`cvd-badge ${expansionStatusBadgeClass(batch.status)}`}>
                    {translateEnum(t, `${COPY_PREFIX}.batchStatus`, batch.status)}
                  </span>
                </header>
                <dl className="cvd-expansion-hub__batch-meta">
                  <div>
                    <dt>{t(`${COPY_PREFIX}.batches.category`)}</dt>
                    <dd>{categoryLabel(batch.categoryKey)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${COPY_PREFIX}.batches.wave`)}</dt>
                    <dd>{translateEnum(t, `${COPY_PREFIX}.seedWave`, batch.waveKey)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <div className="cvd-expansion-hub__empty">
            <p>{t(`${COPY_PREFIX}.batches.empty`)}</p>
            <p className="muted">{t(`${COPY_PREFIX}.batches.emptyHint`)}</p>
          </div>
        )
      ) : null}
    </ExpansionShell>
  );
}

export default function ClientVendorDirectoryExpansionHubPage() {
  return (
    <Routes>
      <Route index element={<OverviewPage />} />
      <Route path="categorias" element={<CategoriesPage />} />
      <Route path="lotes" element={<BatchesPage />} />
    </Routes>
  );
}
